import {
    SegmentedCarousel,
    SimpleCarousel,
    useSegmentedCarouselController,
} from '@eventbrite/consumer-search.collection-carousel';
import { useIsMobileDevice } from '@eventbrite/legacy-infra.feature-detection';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { Typography, TypographyProps } from '@eventbrite/marmalade';
import React, { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BREAKPOINTS } from '@eventbrite/legacy-eds.styles/breakpoints';
import { useAppSelector } from '@/src/homepage/redux/hooks';
import { selectLocation } from '@/src/homepage/redux/location';
import { Location } from '@/src/homepage/types';
import { Guide, Guides } from '@/src/homepage/types/guides';
import { GuideCard } from '../GuideCard';
import { queryClient } from '../utils/queryClient';
import { useGuides } from './useGuides';
import styles from './GuidesCarousel.module.scss';

const messages = defineMessages({
    defaultTitle: {
        id: 'Hand-picked happenings',
        defaultMessage: 'Hand-picked happenings',
    },
    defaultSubtitle: {
        id: 'Find your next thing to do by exploring curated lists',
        defaultMessage: 'Find your next thing to do by exploring curated lists',
    },
});

const DEFAULT_TITLE = messages.defaultTitle;
const DEFAULT_SUBTITLE = messages.defaultSubtitle;

interface HeaderProps {
    title: MessageDescriptor;
    titleVariant?: TypographyProps['variant'];
    subtitle?: MessageDescriptor;
}

const Header = ({
    title,
    titleVariant = 'display-md',
    subtitle,
    isMobile,
}: HeaderProps & { isMobile?: boolean }) => {
    const intl = useIntl();
    const displayTitle = intl.formatMessage(title);
    const displaySubtitle = subtitle ? intl.formatMessage(subtitle) : undefined;

    return (
        <div className={styles.header}>
            <Typography
                className={styles.carouselTitle}
                variant={titleVariant}
                color="neutral-1000"
                as="h1"
            >
                {displayTitle}
            </Typography>
            {displaySubtitle && (
                <Typography
                    className={styles.carouselSubtitle}
                    variant={isMobile ? 'body-md' : 'body-lg'}
                    color="neutral-1000"
                    as="h2"
                >
                    {displaySubtitle}
                </Typography>
            )}
        </div>
    );
};

const getItems = ({ carouselLoading, items }: { carouselLoading: boolean; items: Guide[] }) => {
    if (carouselLoading) {
        return Array.from({ length: 8 }, (_, i) => <LoadingSkeleton height={320} key={i} />);
    }
    return [...items].map((item) => {
        return (
            <div key={item.guideId}>
                <GuideCard guide={item} />
            </div>
        );
    });
};

interface GuidesCarouselProps {
    /**
     * Whether to show the guides carousel
     */
    showGuidesCarousel?: boolean;
    /**
     * Add a prop to pass in a list of guideIds to filter out of the carousel
     * This is useful for the "More Guides" carousel
     */
    guidesToExclude?: string[];
    place?: Location | null;
    headerProps?: HeaderProps;
    /**
     * Initial guides data loaded server-side (for SSR caching)
     */
    initialGuides?: Guides;
}

export const GuidesCarousel = (props: React.PropsWithChildren<GuidesCarouselProps>) => {
    const { showGuidesCarousel, initialGuides, headerProps, children } = props;

    if (!showGuidesCarousel) {
        return <>{children}</>;
    }

    if (initialGuides?.items?.length) {
        return (
            <BaseGuidesCarousel
                guides={initialGuides}
                isLoading={false}
                headerProps={headerProps ?? { title: DEFAULT_TITLE, subtitle: DEFAULT_SUBTITLE }}
            />
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <GuidesCarouselWithRequest {...props} />
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

export const BaseGuidesCarousel = ({
    guides,
    isLoading,
    headerProps = {
        title: DEFAULT_TITLE,
        subtitle: DEFAULT_SUBTITLE,
    },
}: {
    guides: Guides;
    isLoading: boolean;
    headerProps?: HeaderProps;
}) => {
    const isMobile = false;
    // FIXME Always use DesktopCarousel until we can figure out mobile carousel size & spacing issues.
    // const isMobile = useIsMobileDevice(parseInt(BREAKPOINTS['small-width'], 10));

    const Carousel = isMobile ? MobileCarousel : DesktopCarousel;

    return (
        <div data-testid="guides-carousel" className={styles.root}>
            <div className={styles.carouselContainer}>
                <Carousel guides={guides} isLoading={isLoading} headerProps={headerProps} />
            </div>
        </div>
    );
};

const GuidesCarouselWithRequest = ({
    showGuidesCarousel = false,
    place,
    headerProps,
    guidesToExclude,
    initialGuides,
    children,
}: React.PropsWithChildren<GuidesCarouselProps>) => {
    const location = useAppSelector(selectLocation);

    const placeToUse = place || location;

    const {
        data: guides,
        isLoading,
        isError,
    } = useGuides({
        place: placeToUse,
        guidesToExclude,
        enabled: showGuidesCarousel && !initialGuides,
        initialData: initialGuides,
    });

    if (!showGuidesCarousel) {
        return children;
    }

    const guidesToUse = initialGuides || guides;
    const isActuallyLoading = isLoading && !initialGuides;

    if (isActuallyLoading || (placeToUse?.slug === '' && !initialGuides) || !guidesToUse) {
        return (
            <div data-testid="guides-carousel-loading" className={styles.loadingContainer}>
                <div className={styles.loadingResponsiveWrapper}>
                    <LoadingSkeleton width="100%" height="100%" />
                </div>
            </div>
        );
    }

    /**
     * If there is an error or no guides, return the children
     */
    if (isError || guidesToUse?.items?.length === 0) {
        return children;
    }

    return (
        <BaseGuidesCarousel
            guides={guidesToUse}
            isLoading={isActuallyLoading}
            headerProps={headerProps}
        />
    );
};

interface CarouselProps {
    guides: Guides;
    isLoading: boolean;
    headerProps: HeaderProps;
}

const DesktopCarousel = ({ guides, isLoading, headerProps }: CarouselProps) => {
    const controllerProps = useSegmentedCarouselController();
    const { setShouldUpdateCarousel } = controllerProps;
    const { items } = guides;
    const isMobile = useIsMobileDevice(parseInt(BREAKPOINTS['small-wide-width'], 10));

    useEffect(() => {
        if (items.length > 0) {
            setShouldUpdateCarousel(true);
        }
    }, [items]);

    return (
        <SegmentedCarousel
            {...controllerProps}
            rootPaddingTop={0}
            rootPaddingLeft={isMobile ? 12 : 0}
            rootPaddingRight={isMobile ? 12 : 16}
            rootPaddingBottom={0}
            headerGap={0}
            syncedPadding={isMobile ? 4 : 6}
            tokens={{
                '--SegmentedCarouselBackgroundColor': 'transparent',
            }}
            visibleSegments={isMobile ? 2 : 4}
            controlPosition={isMobile ? 'hidden' : 'top'}
            hideOverflow={!isMobile}
        >
            <SegmentedCarousel.Header>
                <Header isMobile={isMobile} {...headerProps} />
            </SegmentedCarousel.Header>
            <SegmentedCarousel.Track>
                {getItems({ carouselLoading: isLoading, items })}
            </SegmentedCarousel.Track>
            <SegmentedCarousel.Footer />
        </SegmentedCarousel>
    );
};
const MobileCarousel = ({ guides, isLoading, headerProps }: CarouselProps) => {
    return (
        <SimpleCarousel
            headerTitle={<Header {...headerProps} />}
            lazilyRenderOffScreenElements={false}
        >
            {getItems({ carouselLoading: isLoading, items: guides.items })}
        </SimpleCarousel>
    );
};
